// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * AuthenticatedAvatar Component
 *
 * A Mantine Avatar that can display images from Medplum Binary resources
 * by fetching them with authenticated requests.
 */

import { Avatar, type AvatarProps } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { getTenantConfig } from '../config/tenant';

// Get the correct API base URL - use Medplum API directly
const getApiBaseUrl = (): string => {
  // In development, use /medplum/ proxy (configured in vite.config.ts)
  if (import.meta.env.DEV) {
    return `${window.location.origin}/medplum`;
  }
  // Production uses Medplum API directly
  return getTenantConfig().medplumBaseUrl;
};

interface AuthenticatedAvatarProps extends Omit<AvatarProps, 'src'> {
  /** The URL of the image (can be a Medplum Binary URL or data URL) */
  src: string | undefined | null;
  /** Children to display when no image (usually initials) */
  children?: ReactNode;
}

export function AuthenticatedAvatar({
  src,
  children,
  ...avatarProps
}: AuthenticatedAvatarProps): JSX.Element {
  const medplum = useMedplum();
  const [blobUrl, setBlobUrl] = useState<string | undefined>();

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;

    if (!src) {
      setBlobUrl(undefined);
      return;
    }

    // If it's already a data URL, use it directly
    if (src.startsWith('data:')) {
      setBlobUrl(src);
      return;
    }

    // Check if it's a relative Medplum Binary URL (e.g., "Binary/xxx")
    const isRelativeBinaryUrl = src.startsWith('Binary/');

    // For relative Binary URLs OR full URLs that might be Medplum storage URLs,
    // we need to fetch with authentication
    const fetchImage = async (): Promise<void> => {
      try {
        let binaryUrl: string;
        const apiBaseUrl = getApiBaseUrl();
        const accessToken = medplum.getAccessToken();

        if (isRelativeBinaryUrl) {
          const binaryId = src.replace('Binary/', '');
          binaryUrl = `${apiBaseUrl.replace(/\/$/, '')}/fhir/R4/Binary/${binaryId}`;
        } else if (src.startsWith('https://') || src.startsWith('http://')) {
          // Check if it's a Medplum/storage URL that needs authentication
          const tenantConfig = getTenantConfig();
          const isMedplumStorage = src.includes('medplum') || src.includes(tenantConfig.domain) || src.includes('storage.');
          // Check if it's a localhost API URL (development)
          const isLocalhostApi = src.includes('localhost') && src.includes('/fhir/R4/Binary/');

          // Extract Binary ID from the URL
          const binaryIdMatch = src.match(/Binary\/([a-f0-9-]+)/i);

          if (binaryIdMatch) {
            // URL contains a Binary reference - fetch via API to get presigned URL or download
            binaryUrl = `${apiBaseUrl.replace(/\/$/, '')}/fhir/R4/Binary/${binaryIdMatch[1]}`;
          } else if (!isMedplumStorage && !isLocalhostApi) {
            // Truly external URL (like example.com) - use directly
            setBlobUrl(src);
            return;
          } else {
            // Medplum storage URL without Binary ID - try to use directly (it might be a presigned URL)
            setBlobUrl(src);
            return;
          }
        } else {
          // Unknown URL format - try to use directly
          setBlobUrl(src);
          return;
        }

        // Fetch with the access token and Accept header to get JSON metadata (not raw binary)
        const headers: Record<string, string> = {
          Accept: 'application/fhir+json',
        };
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }

        const response = await fetch(binaryUrl, { headers });

        if (!response.ok) {
          throw new Error(`Failed to fetch Binary: ${response.status}`);
        }

        const binary = await response.json();

        if (cancelled) return;

        if (binary.url) {
          // Use the presigned URL directly
          setBlobUrl(binary.url);
        } else {
          // Fallback: try to get content as blob using medplum.download
          const downloadSrc = isRelativeBinaryUrl ? src : `Binary/${binary.id}`;
          const blob = await medplum.download(downloadSrc);
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        }
      } catch {
        // Silently fail - avatar will show fallback (initials)
        if (!cancelled) {
          setBlobUrl(undefined);
        }
      }
    };

    fetchImage();

    // Cleanup function
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, medplum]);

  return (
    <Avatar {...avatarProps} src={blobUrl}>
      {children}
    </Avatar>
  );
}
